<script setup>
import { ref, computed } from 'vue';
import { Mail, Lock, Phone, Ticket, Eye, EyeOff } from 'lucide-vue-next';
import { cn } from '@/lib/utils';

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: 'text',
    validator: (value) => ['text', 'email', 'tel', 'password'].includes(value),
  },
  icon: {
    type: String,
    default: null,
    validator: (value) => ['mail', 'lock', 'phone', 'ticket'].includes(value),
  },
  label: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  required: {
    type: Boolean,
    default: false,
  },
  maxlength: {
    type: [String, Number],
    default: null,
  },
  autocomplete: {
    type: String,
    default: null,
  },
  name: {
    type: String,
    default: null,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  showPasswordToggle: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue']);

const inputRef = ref(null);
const showPassword = ref(false);

const inputValue = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
});

const inputType = computed(() => {
  if (props.type === 'password' && props.showPasswordToggle) {
    return showPassword.value ? 'text' : 'password';
  }
  return props.type;
});

const iconComponent = computed(() => {
  const iconMap = {
    mail: Mail,
    lock: Lock,
    phone: Phone,
    ticket: Ticket,
  };
  return iconMap[props.icon];
});

const focus = () => {
  inputRef.value?.focus();
};

const scrollIntoView = (options) => {
  inputRef.value?.scrollIntoView(options);
};

defineExpose({
  focus,
  scrollIntoView,
});
</script>

<template>
  <div class="space-y-2">
    <label v-if="label" class="text-sm font-light text-black/60">{{ label }}</label>
    <div class="relative">
      <component
        :is="iconComponent"
        v-if="icon"
        class="absolute left-4 z-10 top-1/2 -translate-y-1/2 h-4 w-4 text-black/80"
      />
      <input
        ref="inputRef"
        v-model="inputValue"
        :type="inputType"
        :required="required"
        :maxlength="maxlength"
        :autocomplete="autocomplete"
        :name="name"
        :disabled="disabled"
        :class="
          cn(
            'w-full py-2.5 border border-black/10 rounded-[12px] text-black placeholder-black/30 focus:ring-2 focus:ring-black/10 focus:border-black/20 focus:outline-none transition-all duration-300 bg-white/70 backdrop-blur-sm font-light',
            icon ? 'pl-11' : 'pl-4',
            showPasswordToggle && type === 'password' ? 'pr-11' : 'pr-4',
            disabled && 'opacity-50 cursor-not-allowed',
          )
        "
        :placeholder="placeholder"
      />
      <button
        v-if="showPasswordToggle && type === 'password'"
        type="button"
        class="absolute right-4 top-1/2 -translate-y-1/2 text-black/40 hover:text-black/60 transition-colors"
        @click="showPassword = !showPassword"
      >
        <Eye v-if="!showPassword" class="h-4 w-4" />
        <EyeOff v-else class="h-4 w-4" />
      </button>
    </div>
  </div>
</template>
